import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';

/**
 * DeliveryConfigStep - Step 4: Configure delivery endpoint.
 */
const DeliveryConfigStep = ({ wizard, api }) => {
  const [formData, setFormData] = useState({
    deliveryUrl: '',
    apiSigningKey: '',
    retryAttempts: 3,
    timeoutSeconds: 120,
    retryDelaySeconds: 60
  });
  const [result, setResult] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);

  // Pre-populate with environment defaults on mount
  useEffect(() => {
    if (!defaultsLoaded) {
      setDefaultsLoaded(true);
      api.getDeliveryDefaults().then(defaults => {
        setFormData({
          deliveryUrl: defaults.deliveryUrl || 'https://',
          apiSigningKey: defaults.apiSigningKey || '',
          retryAttempts: defaults.retryAttempts || 3,
          timeoutSeconds: defaults.timeoutSeconds || 120,
          retryDelaySeconds: defaults.retryDelaySeconds || 60
        });
      }).catch(() => {
        // If defaults fail, keep empty form
      });
    }
  }, [defaultsLoaded, api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    if (!formData.deliveryUrl.startsWith('https://') || formData.deliveryUrl.length < 10) {
      setValidationError('Delivery URL must be a valid HTTPS URL');
      return;
    }

    try {
      const data = await api.createDeliveryConfig(wizard.sessionId, formData);
      setResult(data);
      wizard.completeStep(4);
    } catch {
      // Error shown by hook
    }
  };

  if (result) {
    return (
      <Alert variant="success">
        <Alert.Heading>Delivery Configuration Saved</Alert.Heading>
        <p><strong>URL:</strong> {result.deliveryUrl}</p>
        <p><strong>Retry Attempts:</strong> {result.retryAttempts}</p>
        <p><strong>Timeout:</strong> {result.timeoutSeconds}s</p>
        <p><strong>Retry Delay:</strong> {result.retryDelaySeconds}s</p>
        <p className="text-muted mb-0">Click "Next" to continue.</p>
      </Alert>
    );
  }

  return (
    <div>
      <h5>Step 4: Delivery Configuration</h5>
      <p className="text-muted">Configure the delivery endpoint for extracted invoice data.</p>

      {api.error && <Alert variant="danger" dismissible onClose={api.clearError}>{api.error}</Alert>}
      {validationError && <Alert variant="warning">{validationError}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Delivery URL *</Form.Label>
          <Form.Control
            type="url"
            value={formData.deliveryUrl}
            onChange={(e) => setFormData(prev => ({ ...prev, deliveryUrl: e.target.value }))}
            placeholder="https://api.example.com/delivery"
            disabled={api.loading}
          />
          <Form.Text className="text-muted">Must be a valid HTTPS URL</Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>API Signing Key</Form.Label>
          <Form.Control
            type="password"
            value={formData.apiSigningKey}
            onChange={(e) => setFormData(prev => ({ ...prev, apiSigningKey: e.target.value }))}
            placeholder="Enter signing key (optional)"
            disabled={api.loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Retry Attempts</Form.Label>
          <Form.Control
            type="number"
            value={formData.retryAttempts}
            onChange={(e) => setFormData(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) || 0 }))}
            min={0} max={10}
            disabled={api.loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Timeout (seconds)</Form.Label>
          <Form.Control
            type="number"
            value={formData.timeoutSeconds}
            onChange={(e) => setFormData(prev => ({ ...prev, timeoutSeconds: parseInt(e.target.value) || 30 }))}
            min={10} max={300}
            disabled={api.loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Retry Delay (seconds)</Form.Label>
          <Form.Control
            type="number"
            value={formData.retryDelaySeconds}
            onChange={(e) => setFormData(prev => ({ ...prev, retryDelaySeconds: parseInt(e.target.value) || 10 }))}
            min={5} max={300}
            disabled={api.loading}
          />
        </Form.Group>

        <Button type="submit" variant="primary" disabled={api.loading}>
          {api.loading ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Save Configuration'}
        </Button>
      </Form>
    </div>
  );
};

export default DeliveryConfigStep;
